import React from 'react'
import { api } from '../../api/client'
import type { Billing, BillingInterval } from '../../api/client'
import { useApp } from '../../context/AppContext'
import { openPortal } from '../../billing/portal'
import { rememberPending } from '../../billing/pending'
import { scrollToCardPayments, useCardAccount } from '../../payments/useCardAccount'
import { startPayments } from '../../billing/startPayments'
import { Icon } from '../../ui/Icon'
import CancelProSheet from '../../billing/CancelProSheet'
import ChargeConfirm from '../../billing/ChargeConfirm'
import TurnOffPaymentsSheet from '../../billing/TurnOffPaymentsSheet'
import {
  CANCEL_PRO,
  CANCELLED,
  CARD_AND_INVOICES,
  OVERDUE_LINE,
  PAY_YEARLY,
  PAY_YEARLY_CONFIRM,
  RUNGS,
  TURN_OFF_CARD_PAYMENTS,
  UPGRADE_CONFIRM_BUTTON,
  VAT_LINE,
  amountLine,
  buyLabel,
  endingLine,
  keepLabel,
  longDate,
  paymentsButton,
  paymentsNote,
  paymentsStage,
  proButton,
  proNote,
  priceLine,
  proOffer,
  rungName,
  upgradeConfirm,
  upgradesInPlace,
  youreOn,
} from '../../billing/rungs'
import type { PaidRung } from '../../api/client'
import { overdue, reached, useBilling } from '../../billing/useBilling'
import Panel from './Panel'
import { saveErrorMessage } from './toast'
import '../../billing/billing.css'

// headline is the rung and what she pays for it: "Free", "Payments, £14 a
// month", "Pro, free until 30 April 2027" (a grant, or a founder's free months).
function headline(b: Billing, timeZone?: string): string {
  const name = rungName(b.rung)
  if (b.source === 'grant') return b.grant_until ? `${name}, free until ${longDate(b.grant_until)}` : `${name}, free`
  const sub = b.subscription
  // Right after Checkout the trial's end may not have arrived from Stripe yet.
  if (b.source === 'subscription' && sub?.trialing) return sub.current_period_end ? `${name}, free until ${longDate(sub.current_period_end, timeZone)}` : `${name}, free`
  if (b.source === 'subscription' && sub) return `${name}, ${amountLine(sub.amount_pence, sub.interval)}`
  return name
}

// periodLine is when she is next charged (after free months, what she will
// pay), or when a cancelled rung ends.
function periodLine(b: Billing, timeZone?: string): string | null {
  const sub = b.subscription
  if (b.source !== 'subscription' || !sub?.current_period_end) return null
  const on = longDate(sub.current_period_end, timeZone)
  if (sub.cancel_at_period_end) return endingLine(b.rung as PaidRung, on)
  if (sub.trialing) return `Then ${amountLine(sub.amount_pence, sub.interval)}`
  return `Next charge on ${on}`
}

type Ask = 'yearly' | 'upgrade' | 'cancel' | 'turn-off' | null

// Rung is one rung on the Free plan card (owner, 2026-10-25): its name and
// price, what it includes (brand/rungs.json, as the pricing page and the
// sheets), a line on where she stands, and the one button that buys it. A
// founder's Pro is her offer (monthly only, free months first).
function Rung({ rung, billing, note, button }: { rung: PaidRung; billing: Billing; note: string | null; button: React.ReactNode | null }) {
  const price =
    rung !== 'pro' ? priceLine(rung) : billing.founder_member ? proOffer(billing) : `${priceLine('pro')}, or ${priceLine('pro', 'year')}`
  return (
    <section className="plan-rung" aria-label={rungName(rung)}>
      <h3 className="plan-rung__name">
        {rungName(rung)} <span className="plan-rung__price">{price}</span>
      </h3>
      <p className="plan-rung__tag">{RUNGS[rung].tagline}</p>
      <ul className="rung-sheet__includes plan-rung__includes">
        {RUNGS[rung].includes.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {note && <p className="settings-intro plan-rung__note">{note}</p>}
      {button}
    </section>
  )
}

// PlanPanel is Settings → Your plan (spec section 5): the rung and its price,
// or her founder or grant terms; the next charge; the buttons her state
// allows; and the failed-payment line while a payment is overdue. Her card
// and invoices are Stripe's portal (Card and invoices); everything that changes
// the plan (moving up, paying yearly, cancelling, keeping card payments when
// Pro goes, taking a cancellation back) goes through the API, so the words
// are Harmovi's and cancelling Pro always asks about card payments first.
export default function PlanPanel() {
  const { state } = useApp()
  const { billing, follow } = useBilling()
  const account = useCardAccount()
  const [ask, setAsk] = React.useState<Ask>(null)
  const [busy, setBusy] = React.useState(false)
  const [done, setDone] = React.useState<string | null>(null)
  // settling is while the app waits for Stripe to confirm a change: the
  // buttons of the old plan would be wrong, so none show until it has.
  const [settling, setSettling] = React.useState(false)
  const [error, setError] = React.useState('')

  // changed says what she just did and follows Stripe until her plan shows
  // it; the line stays, above the new plan's buttons (review, 2026-10-25:
  // it used to replace them, Keep Pro included, until she left the page).
  const changed = (line: string, until: (b: Billing | undefined) => boolean) => {
    setDone(line)
    setSettling(true)
    void follow(until).then(() => setSettling(false))
  }

  if (!billing) return null
  const sub = billing.subscription
  const subscribed = billing.source === 'subscription' && sub !== null
  const coachSubscription = subscribed && billing.rung === 'pro'
  const late = overdue(billing)
  // Nothing new is sold on a card that just failed (review, 2026-10-25):
  // while a payment is overdue the card's one job is updating the card.
  // Pro waits for Stripe's approval (owner, 2026-10-25); until then the card
  // says so, and the Card payments card below has the setup.
  const waitsForStripe = paymentsStage(account) !== 'buy'
  const canGoCoach = !billing.entitlements.pro && !late && !waitsForStripe
  // A founder pays her founder price monthly; there is no founder yearly
  // price, so yearly is not offered to her (the API refuses it too).
  const canPayYearly = coachSubscription && sub!.interval === 'month' && !sub!.cancel_at_period_end && !billing.founder_member && !late
  const timeZone = state.me?.professional.timezone
  const period = periodLine(billing, timeZone)
  const endsOn = sub?.current_period_end ? longDate(sub.current_period_end, timeZone) : null

  const attempt = async (work: () => Promise<void>) => {
    setBusy(true)
    setError('')
    try {
      await work()
    } catch (e) {
      // Back to the plan's buttons, so Card and invoices is there to fix a
      // declined card; the Cancel Pro sheet shows the error where she is.
      setAsk((a) => (a === 'cancel' || a === 'turn-off' ? a : null))
      setError(saveErrorMessage(e))
    }
    setBusy(false)
  }

  const goCoach = (interval?: BillingInterval) =>
    attempt(async () => {
      const result = await api.startCheckout(interval ? { rung: 'pro', interval } : { rung: 'pro' })
      if (result.url) {
        rememberPending('pro')
        window.location.assign(result.url)
        return
      }
      setAsk(null)
      if (interval === 'year') changed('You’re on Pro, paid yearly.', (b) => b?.subscription?.interval === 'year')
      else changed(`${youreOn('pro')}.`, (b) => reached(b, 'pro'))
    })

  const keepCardPayments = () =>
    attempt(async () => {
      await api.cancelSubscription('card_payments')
      setAsk(null)
      changed(`You’re on ${rungName('card_payments')}.`, (b) => b?.rung === 'card_payments')
    })

  // Cancel at period end: she keeps what she paid for until the date.
  const cancelBoth = () =>
    attempt(async () => {
      await api.cancelSubscription()
      setAsk(null)
      changed(CANCELLED, (b) => b?.subscription?.cancel_at_period_end === true)
    })

  // Keep Pro / Keep card payments on: the cancellation taken back.
  const resume = () =>
    attempt(async () => {
      await api.resumeSubscription()
      changed(billing.rung === 'pro' ? 'You’re staying on Pro.' : 'Card payments stay on.', (b) => b?.subscription?.cancel_at_period_end === false)
    })

  const toBilling = () => attempt(openPortal)

  // Free → Payments, in the order the spec sets (section 1.1): Stripe's setup
  // first, which is free, then Checkout for the £14 once Stripe has approved
  // her. Before that the button takes her down to the Card payments card,
  // whose own button starts the setup (owner, 2026-10-25).
  const toPayments = () =>
    attempt(async () => {
      const step = await startPayments(account)
      if (step === 'setup' || step === 'pending') scrollToCardPayments()
      if (step === 'done') changed(`${youreOn('card_payments')}.`, (b) => reached(b, 'card_payments'))
    })

  // Pro without a separate Payments subscription takes card payments with
  // it, so Cancel Pro asks first (spec 1.4), unless a grant (Payments or
  // Pro) keeps card payments on after Pro ends.
  const grantKeepsPayments = billing.grant_rung === 'card_payments' || billing.grant_rung === 'pro'
  const cancelPro = () => {
    if (grantKeepsPayments) void cancelBoth()
    else setAsk('cancel')
  }
  // Both stay while a payment is overdue (owner, 2026-10-25): hiding them left
  // her no way out anywhere, since the portal cannot cancel. The sheets say the
  // failed payment is still due.
  const canCancelPro = coachSubscription && !sub!.cancel_at_period_end
  // On Payments the same place turns card payments off (owner, 2026-10-25).
  const canTurnOff = subscribed && billing.rung === 'card_payments' && !sub!.cancel_at_period_end
  const canResume = subscribed && sub!.cancel_at_period_end

  // Going Pro from a Payments subscription charges her card at once (the
  // API changes the subscription in place), so she is asked first.
  const startGoCoach = () => {
    if (upgradesInPlace(billing)) setAsk('upgrade')
    else void goCoach()
  }

  const actions = () => {
    if (ask === 'upgrade') {
      return (
        <ChargeConfirm
          label={buyLabel('pro', billing)}
          line={upgradeConfirm(endsOn)}
          confirm={UPGRADE_CONFIRM_BUTTON}
          busy={busy}
          onConfirm={() => goCoach()}
          onCancel={() => setAsk(null)}
        />
      )
    }
    if (ask === 'yearly') {
      return (
        <ChargeConfirm
          label={PAY_YEARLY}
          line={PAY_YEARLY_CONFIRM}
          confirm="Pay yearly"
          busy={busy}
          onConfirm={() => goCoach('year')}
          onCancel={() => setAsk(null)}
        />
      )
    }
    // One filled button, the likeliest next step (review, 2026-10-25): taking
    // back a cancellation while it is ending, updating the card while a
    // payment is overdue, else going Pro.
    const keep = canResume && (
      <button type="button" className="btn btn-primary btn-block" onClick={resume} disabled={busy}>
        {keepLabel(billing.rung as 'pro' | 'card_payments')}
      </button>
    )
    const fixCard = sub && (
      <button type="button" className={late ? 'btn btn-primary btn-block' : 'btn btn-secondary btn-block'} onClick={toBilling} disabled={busy}>
        {CARD_AND_INVOICES}
      </button>
    )
    if (!keep && !fixCard && !canGoCoach) return null
    return (
      <div className="plan-actions">
        {keep}
        {late && fixCard}
        {canGoCoach && (
          <button type="button" className={canResume ? 'btn btn-secondary btn-block' : 'btn btn-primary btn-block'} onClick={startGoCoach} disabled={busy}>
            {buyLabel('pro', billing)}
          </button>
        )}
        {canPayYearly && (
          <button type="button" className="btn btn-secondary btn-block" onClick={() => setAsk('yearly')} disabled={busy}>
            {PAY_YEARLY}
          </button>
        )}
        {!late && fixCard}
        {canCancelPro && (
          <button type="button" className="btn btn-danger btn-compact plan-cancel" onClick={cancelPro} disabled={busy}>
            {CANCEL_PRO}
          </button>
        )}
        {canTurnOff && (
          <button type="button" className="btn btn-danger btn-compact plan-cancel" onClick={() => setAsk('turn-off')} disabled={busy}>
            {TURN_OFF_CARD_PAYMENTS}
          </button>
        )}
      </div>
    )
  }

  // On Free the card is the choice (owner, 2026-10-25): both paid rungs with
  // what each includes and its button, so Payments is not found only after
  // Stripe's setup, and Pro says that payments come with it.
  if (billing.rung === 'free' && !billing.entitlements.card_payments && !billing.entitlements.pro) {
    // While Stripe checks her details nothing she taps moves it on, so the
    // Payments rung has its line and no button.
    const paymentsLabel = paymentsButton(account, false)
    const toSetupBelow = paymentsStage(account) === 'setup' || paymentsStage(account) === 'finish'
    // Pro waits for Stripe's approval like Payments (owner, 2026-10-25): until
    // then its rung has the line saying so and the same button as Payments,
    // which takes her down to the Card payments card.
    const proLabel = proButton(account, false)
    return (
      <Panel title="Your plan">
        <p className="settings-row__title">{headline(billing, timeZone)}</p>
        <p className="settings-intro">{RUNGS.free.tagline}</p>
        {done ? (
          <p className="settings-intro plan-done" role="status">{done}</p>
        ) : (
          <div className="plan-rungs">
            <Rung
              rung="card_payments"
              billing={billing}
              note={paymentsNote(account)}
              button={
                paymentsLabel &&
                (toSetupBelow ? (
                  // Before approval the setup's home is the Card payments card
                  // below, with what Stripe asks for and the filled button that
                  // starts it (owner, 2026-10-25): this one takes her there.
                  <button type="button" className="btn btn-secondary btn-block" onClick={toPayments} disabled={busy}>
                    {paymentsLabel} <Icon name="chevron-down" size={18} />
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary btn-block" onClick={toPayments} disabled={busy}>
                    {paymentsLabel}
                  </button>
                ))
              }
            />
            <Rung
              rung="pro"
              billing={billing}
              note={proNote(account)}
              button={
                // One filled button on the card (docs/25 P1; owner,
                // 2026-10-25): the Payments step, her likeliest first need;
                // Pro outlined beside it (the locks sell Pro across the app).
                proLabel &&
                (toSetupBelow ? (
                  <button type="button" className="btn btn-secondary btn-block" onClick={toPayments} disabled={busy}>
                    {proLabel} <Icon name="chevron-down" size={18} />
                  </button>
                ) : (
                  <button type="button" className="btn btn-secondary btn-block" onClick={startGoCoach} disabled={busy}>
                    {proLabel}
                  </button>
                ))
              }
            />
          </div>
        )}
        {error && <p className="field-error" role="alert">{error}</p>}
        {!done && <p className="settings-intro">{VAT_LINE}</p>}
      </Panel>
    )
  }

  // A price is on show unless the plan is free by grant with nothing to buy.
  const priced = subscribed || canGoCoach || ask !== null
  return (
    <Panel title="Your plan">
      <p className="settings-row__title">{headline(billing, timeZone)}</p>
      {period && <p className="settings-intro">{period}</p>}
      {billing.founder_member && <p className="settings-intro">Founder member</p>}
      {late && <p className="plan-overdue">{OVERDUE_LINE}</p>}
      {!billing.entitlements.pro && !late && waitsForStripe && proNote(account) && <p className="settings-intro">{proNote(account)}</p>}
      {done && <p className="settings-intro plan-done" role="status">{done}</p>}
      {!settling && actions()}
      {ask === 'cancel' && (
        <CancelProSheet
          endsOn={endsOn}
          overdue={late}
          busy={busy}
          error={error}
          onKeepPayments={keepCardPayments}
          onCancelBoth={cancelBoth}
          onClose={() => { setAsk(null); setError('') }}
        />
      )}
      {ask === 'turn-off' && (
        <TurnOffPaymentsSheet endsOn={endsOn} overdue={late} busy={busy} error={error} onConfirm={cancelBoth} onClose={() => { setAsk(null); setError('') }} />
      )}
      {error && ask !== 'cancel' && ask !== 'turn-off' && <p className="field-error" role="alert">{error}</p>}
      {priced && ask !== 'yearly' && ask !== 'upgrade' && !settling && <p className="settings-intro">{VAT_LINE}</p>}
    </Panel>
  )
}
